/**
 * Lendmarq "Pipeline" nav-tab buckets — three client-side slices of the SAME
 * all-loans payload (losPipelineApi.getLendmarqPipeline), surfaced as the
 * Pipeline dropdown: Active Pipeline / Closed / Dead-Inactive.
 *
 * The three `match` predicates PARTITION the payload — every loan lands in
 * exactly one bucket and nothing is silently dropped:
 *
 *   - Closed         → loanStatus is a closed/secondary-market state, or the
 *                      loan has advanced to the "Closed" pipeline.
 *   - Dead/Inactive  → loanStatus "Dead" or "Inactive".
 *   - Active         → catch-all: everything that hasn't closed and isn't
 *                      dead/inactive (incl. early statuses like "Prospect").
 *
 * Each bucket's `statuses` is ONLY the set of quick-filter chips for the
 * in-toolbar status switcher — it does NOT gate membership (the predicate does).
 */

// loanStatus values that mean the loan has closed (incl. post-close /
// secondary-market states). Matched by name OR by the "Closed" pipeline bucket.
const CLOSED_STATUSES = [
  "Closed",
  "Closed w/ Post Closing Conditions",
  "Loan Sold",
  "Balance Sheet",
];
const DEAD_INACTIVE_STATUSES = ["Dead", "Inactive"];

const isClosed = (d) =>
  d?.pipeline === "Closed" || CLOSED_STATUSES.includes(d?.loanStatus);
const isDeadInactive = (d) => DEAD_INACTIVE_STATUSES.includes(d?.loanStatus);

export const LENDMARQ_PIPELINE_BUCKETS = {
  FULL: {
    key: "FULL",
    label: "Full Pipeline",
    slug: "full",
    statusField: "loanStatus",
    // Full pipeline shows every loan — the quick-filter chips span the whole
    // lifecycle (active funnel + closed + dead/inactive). `match` lets
    // everything through.
    statuses: [
      "Prospect",
      "Terms Accepted",
      "Processing",
      "Underwriting",
      "Condition Clearance",
      "Final Review",
      "Clear To Close",
      "Closed",
      "Dead",
      "Inactive",
    ],
    match: () => true,
  },
  ACTIVE: {
    key: "ACTIVE",
    label: "Active Pipeline",
    slug: "active",
    statusField: "loanStatus",
    // Quick-filter chips for the in-toolbar status switcher. "Prospect" leads
    // the active funnel.
    statuses: [
      "Prospect",
      "Terms Accepted",
      "Processing",
      "Underwriting",
      "Condition Clearance",
      "Final Review",
      "Clear To Close",
    ],
    match: (d) => !isClosed(d) && !isDeadInactive(d),
  },
  CLOSED: {
    key: "CLOSED",
    label: "Closed",
    slug: "closed",
    statusField: "loanStatus",
    statuses: [], // single bucket — no status switcher
    match: isClosed,
  },
  DEAD_INACTIVE: {
    key: "DEAD_INACTIVE",
    label: "Dead/Inactive",
    slug: "dead-inactive",
    statusField: "loanStatus",
    statuses: DEAD_INACTIVE_STATUSES, // toggle between Dead / Inactive
    match: isDeadInactive,
  },
};

/**
 * Filter a fetched pipeline down to the loans belonging to one bucket, using
 * that bucket's `match` predicate. Returns [] for unknown buckets or non-array
 * input.
 */
export const dealsForBucket = (deals, bucketKey) => {
  const bucket = LENDMARQ_PIPELINE_BUCKETS[bucketKey];
  if (!bucket || !Array.isArray(deals)) return [];
  return deals.filter(bucket.match);
};

export default LENDMARQ_PIPELINE_BUCKETS;
